import React, { useCallback, useContext, useEffect, useState, useRef } from 'react'
import { PropTypes } from 'prop-types'
import { useQuery, useMutation } from '@apollo/client'
import { useHistory, useLocation } from 'react-router-dom'
import queryString from 'query-string'
import { useTranslation, Trans } from 'react-i18next'
import { Field } from 'formik'
import { getCountrySelectOptions } from 'lib/helpers'
import { fetchPolicy } from 'lib/apolloFetchPolicy'
import useCurrentUser from 'lib/hooks/useCurrentUser'
import { FINLAND_ID } from 'lib/constants'
import { useDebouncedCallback } from 'use-debounce'
import { getModalUrl } from 'lib/modals'
import cookies from 'lib/cookies'
import classNames from 'classnames'
import { getLocale } from 'lib/utils/getLocale'

import Input from 'components/Forms/FormElements/Input'
import Select from 'components/Forms/FormElements/Select'
import { ExtendedFormikContext } from 'components/Forms/ExtendedFormik'
import FormElementGroup from 'components/FormElements/FormElementGroup'
import { validateZip } from 'components/FormElements/Validation'
import LoadingIndicator from 'components/LoadingIndicator'
import ErrorComponent from 'components/Errors/ErrorComponent'
import client from 'components/Client'
import PhoneNumberField from 'components/Forms/FormElements/PhoneNumberField'
import AppWindowBreakpoints from 'components/Util/WindowBreakpoints/AppWindowBreakpoints'
import PhoneStatusTag from 'components/UI/PhoneStatusTag/PhoneStatusTag'
import UserEditMessage from 'components/User/UserEdit/UserEditMessage/UserEditMessage'
import { REDIRECT_USER_TYPE } from 'components/User/UserEdit/UserEdit'
import Link from 'components/Link'
import Button from 'components/UI/Button'
import Icon from 'components/Icon'

import getOptionsCountry from 'graphql/options/getOptionsCountry.gql'
import sendVerificationCode from 'graphql/user/sendVerificationCode.gql'
import mobileAndAdExist from 'graphql/user/CheckUserInfo.gql'
import postalCodeService from 'graphql/user/postalCodeService.gql'

const UserPrimaryDataSection = ({
    zipCodeError,
    setZipCodeError,
    isFormReset,
    setIsFormReset,
    zipCodeLoading,
    setZipCodeLoading,
    errorMobileAndAdExist,
    setErrorMobileAndAdExist,
    errorSendVerificationCode,
    setErrorSendVerificationCode,
}) => {
    const phoneRef = useRef(null)

    const currentUser = useCurrentUser()
    const location = useLocation()
    const history = useHistory()
    const { t, i18n } = useTranslation('editProfile')

    const user = currentUser.me.user
    const [showTownField, setShowTownField] = useState(false)
    const [isMobileVerified, setIsMobileVerified] = useState(user.isMobileVerified)
    const [userTown, setUserTown] = useState(user.town && user.town.en)
    const [county, setCounty] = useState(user.region && user.region.en)
    const [query] = useState(queryString.parse(location.search))
    const [sendPhoneVerificationCode, { loading: sendVerificationCodeLoading, error: sendVerificationCodeError }] = useMutation(sendVerificationCode)
    const { extendedActions, setFieldValue, values, errors, handleChange, setTouched } = useContext(ExtendedFormikContext)

    /**
     * isMobileVerificationRequired(boolean): Verification is mandatory or not when user's redirect type is AD_POST or MOBILE_VERIFICATION
     * isMobileVerified(boolean): Mobile number is verified or not
     */
    const mobileNotVerified = ((query?.type === REDIRECT_USER_TYPE.AD_POST || query?.type === REDIRECT_USER_TYPE.MOBILE_VERIFICATION) && user.isMobileVerificationRequired) ||
        (query?.type === REDIRECT_USER_TYPE.BOARDING_VERIFICATION && !user.isMobileVerified)

    useEffect(() => {
        if (mobileNotVerified && Object.keys(errors).length === 0) {
            setTimeout(() => {
                phoneRef.current?.scrollIntoView()
                extendedActions.focusField('mobile')
                setTouched({ mobile: true })
            }, 100)
        }
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        setIsMobileVerified(user.isMobileVerified)
    }, [user.mobileNumber, user.isMobileVerified])

    useEffect(() => {
        if (user.country && parseInt(user.country.id) !== FINLAND_ID) {
            setShowTownField(true)
        }

        if (isFormReset) {
            setZipCodeError(false)
            setUserTown(user.town?.en)
            setCounty(user.region?.en)
            if (user.country && parseInt(user.country.id) === FINLAND_ID) {
                setShowTownField(false)
            }
            setIsFormReset(false)
        }
    }, [user.country, isFormReset, setIsFormReset, setZipCodeError, user.town?.en, user.region?.en])

    const [handleTownAndCountyDebounced] = useDebouncedCallback((zipcode) => {
        if (
            parseInt(values.country) === FINLAND_ID &&
            zipcode && !isNaN(zipcode) && zipcode.length === 5
        ) {
            setZipCodeLoading(true)
            client.query({
                query: postalCodeService,
                variables: {
                    postCode: zipcode
                },
                fetchPolicy: fetchPolicy.NETWORK_ONLY,
            }).then((result) => {
                const { cityName, countyName, town, region } = result.data.postalCodeService
                if (cityName === null && countyName === null) {
                    setZipCodeError(true)
                } else {
                    setZipCodeError(false)
                }
                setUserTown(cityName)
                setCounty(countyName)
                setFieldValue('town', (town && town.id) || '')
                setFieldValue('region', (region && region.id) || '')
                setZipCodeLoading(false)
            })
        }
    }, 1000)

    const sendVerifyCode = useCallback(async () => {
        await client.query({
            query: mobileAndAdExist,
            variables: {
                mobileNumber: values.mobile,
                locale: i18n.language
            },
            fetchPolicy: fetchPolicy.NETWORK_ONLY,
        }).then(async (result) => {
            try {
                setErrorMobileAndAdExist(null)
                const { sameNumberExist, activeAdsExist, preventHijack } = result.data.mobileAndAdExist

                const userPhoneVerification = {
                    phoneNumber: values.mobile
                }

                const userTransferPhoneNumber = {
                    phoneNumber: values.mobile,
                    activeAdsExist: activeAdsExist,
                    preventHijack: preventHijack
                }

                cookies.set('user-phone-verification', userPhoneVerification, { maxAge: 300 })
                cookies.set('user-transfer-phone-number', userTransferPhoneNumber, { maxAge: 300 })

                if (sameNumberExist) {
                    if (!activeAdsExist && !preventHijack) {
                        history.push(getModalUrl(location, 'kayttaja/puhelinnumero/siirra'))
                    } else {
                        history.push(getModalUrl(location, 'kayttaja/puhelinnumero/siirra/varattu'))
                    }
                } else {
                    await sendPhoneVerificationCode({
                        variables: {
                            mobileNumber: values.mobile,
                            locale: i18n.language
                        }
                    }).then((sendCodeResult) => {
                        history.push(getModalUrl(location, 'kayttaja/puhelinnumero/vahvista'))
                        return Promise.resolve(sendCodeResult)
                    }).catch((sendCodeError) => {
                        setErrorSendVerificationCode(sendVerificationCodeError)
                        return Promise.reject(sendCodeError)
                    })
                }
            } catch (error) {
                return Promise.reject(error)
            }
        }).catch((mobileAndAdExistError) => {
            setErrorMobileAndAdExist(mobileAndAdExistError)
        })
    }, [values.mobile, history, location, sendPhoneVerificationCode, setErrorSendVerificationCode, setErrorMobileAndAdExist, sendVerificationCodeError, i18n.language])

    const [handleSendVerifyCode] = useDebouncedCallback(() => {
        if (query?.type === REDIRECT_USER_TYPE.MOBILE_VERIFICATION && values.mobile && user.isMobileVerificationRequired) {
            sendVerifyCode()
        }
    }, 500)

    useEffect(() => {
        handleSendVerifyCode()
    }, [handleSendVerifyCode])

    const { loading: countriesLoading, error: countriesError, data: countriesData } = useQuery(getOptionsCountry, {
        fetchPolicy: fetchPolicy.CACHE_FIRST,
    })

    if (countriesError) return <ErrorComponent error={countriesError} />

    const defaultSelect = ({ value: '', name: t('editProfile:details.primary.fields.country.chooseValue') })

    const userGenderOptions = ([
        { value: '', name: t('editProfile:details.primary.fields.gender.options.notSpecified') },
        { value: 'male', name: t('editProfile:details.primary.fields.gender.options.male') },
        { value: 'female', name: t('editProfile:details.primary.fields.gender.options.female') },
        { value: 'other', name: t('editProfile:details.primary.fields.gender.options.other') }
    ])

    const phoneStatus = <PhoneStatusTag isMobileVerified={Boolean(values.mobile) && Boolean(isMobileVerified)} />

    const legalNameInfo = (
        <Button
            type="button"
            trackName="showLegalNameInfo"
            onClick={() => history.replace(getModalUrl(location, 'nimitiedot'))}
        >
            <Icon name="icon_info_circle" size="m" />
        </Button>
    )

    return (
        <div className="user-primary-data-section">
            <FormElementGroup cssClass="cols-1">
                <Field
                    name="firstName"
                    type="text"
                    label={t('editProfile:details.primary.fields.firstName.label')}
                    description={t('editProfile:details.primary.fields.firstName.description')}
                    component={Input}
                    strongLabel
                    required={extendedActions.isFieldRequired('firstName', values)}
                />
            </FormElementGroup>
            <FormElementGroup cssClass="cols-1">
                <Field
                    name="lastName"
                    type="text"
                    label={t('editProfile:details.primary.fields.lastName.label')}
                    description={t('editProfile:details.primary.fields.lastName.description')}
                    component={Input}
                    strongLabel
                    required={extendedActions.isFieldRequired('lastName', values)}
                />
            </FormElementGroup>
            {query?.isVerificationProcess && (
                <FormElementGroup cssClass="cols-1">
                    <Field
                        name="legalName"
                        type="text"
                        label={t('editProfile:details.primary.fields.legalName.label')}
                        description={legalNameInfo}
                        component={Input}
                        strongLabel
                        required={extendedActions.isFieldRequired('legalName', values)}
                    />
                </FormElementGroup>
            )}
            <FormElementGroup cssClass="cols-1">
                <div className="phone-number-field" ref={phoneRef}>
                    <PhoneNumberField
                        name="mobile"
                        label={t('editProfile:details.primary.fields.mobile.label')}
                        description={phoneStatus}
                        onChange={e => {
                            handleChange(e)
                            if (user.isMobileVerified && e.target.value === user.mobileNumber) {
                                setIsMobileVerified(true)
                                setFieldValue('isMobileVerified', true)
                            } else {
                                setIsMobileVerified(false)
                                setFieldValue('isMobileVerified', false)
                            }
                        }}
                        className={classNames('phone-number-field', {
                            'phone-number-field-mobileNotVerified': mobileNotVerified
                        })}
                        required={extendedActions.isFieldRequired('mobile', values)}
                    />
                </div>
            </FormElementGroup>
            {Boolean(values.mobile) && (!isMobileVerified || user.mobileNumber !== values.mobile) && (
                <UserEditMessage
                    sectionType="UserPrimaryData"
                    onClick={!errors.mobile ? sendVerifyCode : undefined}
                />
            )}
            {sendVerificationCodeLoading && <LoadingIndicator />}
            <FormElementGroup cssClass="cols-1">
                <Field name="country">
                    {(props) => (
                        <Select
                            {...props}
                            label={t('editProfile:details.primary.fields.country.label')}
                            options={countriesLoading ? [] : !user.country
                                ? [defaultSelect, ...getCountrySelectOptions(countriesData.options.country, i18n.language)]
                                : getCountrySelectOptions(countriesData.options.country, i18n.language)}
                            onChange={e => {
                                handleChange(e)
                                setShowTownField(e.target.value && parseInt(e.target.value) !== FINLAND_ID)
                                if (parseInt(e.target.value) === FINLAND_ID && !validateZip(values.zip)) {
                                    setFieldValue('zip', '')
                                    setUserTown(null)
                                    setCounty(null)
                                }
                                if (parseInt(e.target.value) !== FINLAND_ID) {
                                    setZipCodeError(false)
                                    setFieldValue('town', null)
                                    setFieldValue('region', null)
                                }
                                handleTownAndCountyDebounced(values.zip)
                            }}
                            required={extendedActions.isFieldRequired('country', values)}
                            loading={countriesLoading}
                        />
                    )}
                </Field>
            </FormElementGroup>
            <FormElementGroup cssClass="cols-1">
                <Field
                    type="text"
                    name="address"
                    component={Input}
                    label={t('editProfile:details.primary.fields.address.label')}
                    required={extendedActions.isFieldRequired('address', values)}
                />
            </FormElementGroup>
            <FormElementGroup cssClass="cols-1">
                <AppWindowBreakpoints
                    render={({ up }) => up('containerS') && Boolean(zipCodeError) && (
                        <div className="zip-code--error">
                            <span> {t('editProfile:details.primary.fields.zip.error')} </span>
                        </div>
                    )}
                />
                <div className="zip-code">
                    <Field
                        type="text"
                        name="zip"
                        label={t('editProfile:details.primary.fields.zip.label')}
                        component={Input}
                        onChange={e => {
                            handleChange(e)
                            setZipCodeError(false)
                            setUserTown('')
                            handleTownAndCountyDebounced(e.currentTarget.value)
                        }}
                        required={extendedActions.isFieldRequired('zip', values)}
                    />
                    {!!zipCodeLoading && <LoadingIndicator />}
                    {!zipCodeLoading && !showTownField && !!userTown &&
                    <span className="location">{(!!userTown && !!county) && `${userTown}, ${county}`}</span>}
                </div>
                <AppWindowBreakpoints
                    render={({ up }) => up('containerS') ? null : Boolean(zipCodeError) && (
                        <div className="zip-code--error">
                            <span> {t('editProfile:details.primary.fields.zip.error')} </span>
                        </div>
                    )}
                />
                <Field
                    type="hidden"
                    name="town"
                    component={Input}
                />
                <Field
                    type="hidden"
                    name="region"
                    component={Input}
                />
            </FormElementGroup>
            {Boolean(showTownField) && (
                <FormElementGroup cssClass="cols-1">
                    <Field
                        type="text"
                        name="city"
                        component={Input}
                        label={t('editProfile:details.primary.fields.city.label')}
                        required={extendedActions.isFieldRequired('city', values)}
                    />
                </FormElementGroup>
            )}
            <FormElementGroup cssClass="cols-1">
                <Field name="gender">
                    {(props) => (
                        <Select
                            {...props}
                            label={t('editProfile:details.primary.fields.gender.label')}
                            options={userGenderOptions}
                        />
                    )}
                </Field>
            </FormElementGroup>
            <div>{t('editProfile:details.primary.text')}</div>
            {(errorSendVerificationCode || errorMobileAndAdExist) && (
                <ErrorComponent
                    error={errorSendVerificationCode || errorMobileAndAdExist}
                    contentOverride={(appError) => {
                        if (appError.code === 1000) {
                            return (
                                <Trans i18nKey="editProfile:details.primary.fields.mobile.errorMessage">
                                    Tarkista puhelinnumero. Ongelmatilanteissa <Link to={`${getLocale(i18n.language)}/ota-yhteytta#asiakaspalvelu`} trackName="customerService">ota yhteyttä asiakaspalveluun</Link>
                                </Trans>
                            )
                        }
                    }}
                />
            )}
        </div>
    )
}

UserPrimaryDataSection.propTypes = {
    setZipCodeError: PropTypes.func.isRequired,
    zipCodeError: PropTypes.bool,
}

export default UserPrimaryDataSection
